/**
 * Aplicación web Directorio del Ayuntamiento de Zamora.
 * 
 * Desarrollo e implementación: Jaime León Pereira.
 * Ingeniero Informático de la Unidad TIC.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { useMsal } from "@azure/msal-react";

function MiPerfil() {
    const { instance, accounts } = useMsal();
    const [persona, setPersona] = useState(null);
    const [formData, setFormData] = useState({});
    const [departamentos, setDepartamentos] = useState([]);
    const [edificios, setEdificios] = useState([]);
    const [message, setMessage] = useState("");
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const tokenResponse = await instance.acquireTokenSilent({
                    account: accounts[0],
                    scopes: [process.env.REACT_APP_API_SCOPE],
                });

                // Datos del usuario
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/personas/me`,
                    {
                        headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
                    }
                );

                setPersona(res.data);
                setFormData({
                    ...res.data,
                    departamentoId: res.data.departamento?.id || "",
                    edificioId: res.data.edificio?.id || ""
                });

                // Listas de departamentos y edificios
                const depRes = await axios.get(`${process.env.REACT_APP_API_URL}/departamentos`, {
                    headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
                });
                setDepartamentos((Array.isArray(depRes.data.content) ? depRes.data.content : []).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' })));

                const edifRes = await axios.get(`${process.env.REACT_APP_API_URL}/edificios`, {
                    headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
                });
                setEdificios((Array.isArray(edifRes.data) ? edifRes.data : []).sort((a, b) => a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' })));

            } catch (err) {
                console.error(err);
                setError("Error al cargar tus datos");
            }
        };

        if (accounts.length > 0) fetchData();
    }, [accounts, instance]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const tokenResponse = await instance.acquireTokenSilent({
                account: accounts[0],
                scopes: [process.env.REACT_APP_API_SCOPE],
            });

            await axios.post(
                `${process.env.REACT_APP_API_URL}/personas/update-request`,
                {
                    nombre: formData.nombre,
                    apellido1: formData.apellido1,
                    apellido2: formData.apellido2,
                    telefono: formData.telefono,
                    extension: formData.extension,
                    cargo: formData.cargo,
                    departamentoId: formData.departamentoId,
                    edificioId: formData.edificioId,
                },
                {
                    headers: { Authorization: `Bearer ${tokenResponse.accessToken}` },
                }
            );

            setMessage("Solicitud enviada correctamente");
        } catch (err) {
            console.error(err);

            if (err.response?.status === 400) {
                setMessage(err.response.data.message);
            } else if (err.response?.status === 409) {
                setMessage(err.response.data.message);
            } else {
                setMessage("Error al enviar la solicitud");
            }
        }
    };

    if (error) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <div className="card shadow text-center p-4" style={{ maxWidth: "500px", width: "100%" }}>
                
                    <div className="mb-3">
                        <i className="bi bi-exclamation-triangle-fill text-warning" style={{ fontSize: "2rem" }}></i>
                    </div>

                    <h5 className="mb-3">Se ha producido un error</h5>
                    <p className="text-muted">{error}</p>

                    <a
                        href="https://incidencias.zamora.es"
                        target="_blank"
                        rel="noreferrer"
                        className="btn mt-3"
                        style={{ backgroundColor: "#fd7e14", color: "white" }} // naranja Bootstrap
                    >
                    Ir a incidencias
                    </a>
                </div>
            </div>
        );
    }

    if (!persona) return <p>Cargando...</p>;

    return (
        <div className="d-flex justify-content-center">
            <div className="card shadow mb-4 w-100" style={{ maxWidth: "500px" }}>
                <div className="card-header">
                    <h4>Mi perfil</h4>
                </div>
                <div className="card-body">
                    {message && (
                        <div className={`alert ${
                            message.includes("correctamente") ? "alert-success" : "alert-warning"
                        }`}>
                            {message}
                        </div>
                    )}
                    <form onSubmit={handleSubmit}>

                        {/* Nombre */}
                        <input
                            type="text"
                            name="nombre"
                            value={formData.nombre || ""}
                            onChange={handleChange}
                            className="form-control mb-2"
                            placeholder="Nombre"
                            maxLength={50}
                            required
                        />

                        {/* Apellido 1 */}
                        <input
                            type="text"
                            name="apellido1"
                            value={formData.apellido1 || ""}
                            onChange={handleChange}
                            className="form-control mb-2"
                            placeholder="Primer apellido"
                            maxLength={50}
                            required
                        />

                        {/* Apellido 2 */}
                        <input
                            type="text"
                            name="apellido2"
                            value={formData.apellido2 || ""}
                            onChange={handleChange}
                            className="form-control mb-2"
                            placeholder="Segundo apellido"
                            maxLength={50}
                        />

                        {/* Email */}
                        <input
                            type="email"
                            name="email"
                            value={formData.email || ""}
                            onChange={handleChange}
                            className="form-control mb-2"
                            placeholder="Email"
                            maxLength={50}
                            required
                        />

                        {/* Teléfono */}
                        <input
                            type="text"
                            name="telefono"
                            value={formData.telefono || ""}
                            onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, ""); // solo números
                            handleChange({ target: { name: "telefono", value } });
                            }}
                            className="form-control mb-2"
                            placeholder="Teléfono (9 dígitos)"
                            maxLength={9}
                            pattern="\d{9}"
                            inputMode="numeric"
                            required
                        />

                        {/* Extensión */}
                        <input
                            type="text"
                            name="extension"
                            value={formData.extension || ""}
                            onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            handleChange({ target: { name: "extension", value } });
                            }}
                            className="form-control mb-2"
                            placeholder="Extensión"
                            maxLength={4}
                            pattern="[0-9]*"
                            inputMode="numeric"
                        />

                        {/* Cargo */}
                        <input
                            type="text"
                            name="cargo"
                            value={formData.cargo || ""}
                            onChange={handleChange}
                            className="form-control mb-2"
                            placeholder="Cargo"
                            maxLength={50}
                        />

                        {/* Departamento */}
                        <select
                            name="departamentoId"
                            value={formData.departamentoId || ""}
                            onChange={handleChange}
                            className="form-control mb-2"
                        >
                            <option value="">Selecciona un departamento</option>
                            {departamentos.map(dep => (
                            <option key={dep.id} value={dep.id}>{dep.nombre}</option>
                            ))}
                        </select>

                        {/* Edificio */}
                        <select
                            name="edificioId"
                            value={formData.edificioId || ""}
                            onChange={handleChange}
                            className="form-control mb-2"
                        >
                            <option value="">Selecciona un edificio</option>
                            {edificios.map(ed => (
                            <option key={ed.id} value={ed.id}>{ed.nombre}</option>
                            ))}
                        </select>

                        <button className="btn btn-primary">Solicitar cambios</button>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default MiPerfil;